"use client";

import { useState, type ReactNode } from "react";
import { ChevronDown, MapPin } from "lucide-react";
import { Typography } from "./Typography";
import { TruncatedField } from "./TruncatedField";
import { BacktrackableText } from "./BacktrackableText";
import { cn } from "@/lib/utils/cn";

interface LocationAccordionProps {
  locationNumber: number | null;
  address?: string;
  descriptionOfOperations?: string;
  natureOfBusiness?: string;
  /** Extra elements rendered at the right end of the header (badges, counts, etc.) */
  headerRight?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
  /** Click handler for the address field (highlights source in PDF) */
  onAddressClick?: () => void;
  /** Click handler for the Operations (description_of_operations) field */
  onDescriptionClick?: () => void;
  /** Click handler for the Nature of Business field */
  onNatureOfBusinessClick?: () => void;
  /**
   * Controlled open state (BAR-238). Pass `isOpen` **and** `onToggle` together to
   * let a parent own expansion — needed for "Expand/Collapse All" across a list of
   * locations, which an accordion holding its own state cannot support.
   *
   * Omit both and it stays uncontrolled, seeded by `defaultOpen`. That is the
   * default on purpose: five existing consumers rely on it and none of them
   * should have to change.
   */
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  /**
   * Render the Operations / Nature of Business strip. Default `true`.
   *
   * Bar-tavern locations carry neither field in their editor — the strip would be
   * two permanently empty columns, one of them showing a red "required" warning
   * for data that premises simply do not have there.
   */
  showOperations?: boolean;
  /**
   * Secondary text after the location number — the mockup's "DBA · address" line.
   * Rendered instead of `address` when supplied.
   */
  secondaryLabel?: ReactNode;
  /**
   * Drop the header row, leaving a plain bordered card. Default `false`; the six existing
   * consumers are unchanged.
   *
   * For a caller that already names the premises outside this card — Bar & Tavern's tab strip
   * does. The body is then FORCED open: with no header there is no control left to reopen it.
   */
  hideHeader?: boolean;
  /**
   * Extra classes on the outer card, so a caller can square off the edge where this meets
   * something above it — the Bar & Tavern locations banner sits directly on top of the selected
   * premises. Default undefined; the five existing consumers are unchanged.
   */
  className?: string;
}

export function LocationAccordion({
  locationNumber,
  address,
  descriptionOfOperations,
  natureOfBusiness,
  headerRight,
  defaultOpen = false,
  children,
  onAddressClick,
  onDescriptionClick,
  onNatureOfBusinessClick,
  isOpen: controlledIsOpen,
  onToggle,
  showOperations = true,
  secondaryLabel,
  hideHeader = false,
  className,
}: LocationAccordionProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);

  // Controlled only when `isOpen` is actually supplied. Checking the prop rather
  // than `onToggle` means a consumer can pass a toggle handler purely to observe
  // clicks while still letting the accordion own its state.
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

  // Forced open with no header, or the card is an empty box with no way out. Here rather than at
  // the call site: an "expand/collapse all" can close every card while the header is gone.
  const isBodyVisible = hideHeader || isOpen;

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setUncontrolledIsOpen(next);
    onToggle?.(next);
  };

  return (
    <div
      className={cn(
        "border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm",
        className,
      )}
    >
      {/* UNMOUNTED, not `hidden`: this row is all derived from props, so there is no state to
          lose — and `hidden` on a `flex` element depends on Preflight's `!important` variant
          beating the utility class. */}
      {!hideHeader && (
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggle();
            }
          }}
          className="w-full flex items-center flex-wrap gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <ChevronDown
            className={cn("h-4 w-4 text-gray-400 transition-transform", isOpen && "rotate-180")}
          />
          <MapPin className="h-3.5 w-3.5 text-gray-500" />
          <Typography as="span" variant="body" weight="semibold">
            Location {locationNumber ?? "—"}
          </Typography>
          {secondaryLabel ? (
            <span className="min-w-0 flex-shrink">{secondaryLabel}</span>
          ) : (
            address && (
              <BacktrackableText onClick={onAddressClick}>
                {address}
              </BacktrackableText>
            )
          )}
          {headerRight && (
            <span className="ml-auto flex items-center gap-2 flex-shrink-0">
              {headerRight}
            </span>
          )}
        </div>
      )}

      {isBodyVisible && (
        <div className="px-4 py-3">
          {showOperations && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-4 pb-3 border-b border-gray-100">
              <TruncatedField
                label="Operations"
                value={descriptionOfOperations}
                onClick={descriptionOfOperations ? onDescriptionClick : undefined}
              />
              <TruncatedField
                label="Nature of Business"
                value={natureOfBusiness}
                required
                onClick={natureOfBusiness ? onNatureOfBusinessClick : undefined}
              />
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
